import { Timestamp } from 'firebase/firestore';

export enum FundingProgram {
  SBA_MICROLOAN = 'sba_microloan',
  CRF_USA = 'crf_usa',
  WOMENVENTURE = 'womenventure',
  MEDA = 'meda',
  NEON = 'neon',
  LAUNCH_MN = 'launch_mn',
  HELLO_ALICE = 'hello_alice',
  OTHER = 'other',
}

export enum FundingApplicationStatus {
  DRAFT = 'draft',
  SUBMITTED = 'submitted',
  UNDER_REVIEW = 'under_review',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  WITHDRAWN = 'withdrawn',
  FUNDED = 'funded',
}

export enum FundingType {
  LOAN = 'loan',
  GRANT = 'grant',
  BOTH = 'both',
}

export interface FundingApplication {
  id?: string;
  
  // Basic Information
  program: FundingProgram;
  programName: string; // Custom name if OTHER
  fundingType: FundingType;
  status: FundingApplicationStatus;
  
  // Application Details
  applicationDate: Date | Timestamp;
  submissionDate?: Date | Timestamp;
  decisionDate?: Date | Timestamp;
  fundingDate?: Date | Timestamp;
  
  // Amount Information
  requestedAmount: number;
  approvedAmount?: number;
  fundedAmount?: number;
  
  // Use of Funds
  useOfFunds: string; // Detailed description
  monthlyBurn?: number; // Monthly cloud + SaaS burn
  cloudAiSubscriptions?: string; // Breakdown of cloud/AI subscriptions (Cursor Ultra, ChatGPT Pro, Firebase Blaze, etc.)
  runwayMonths?: number; // How many months of runway needed
  earliestRevenueTrigger?: string; // Pilot, employer signup, LOI, etc.
  
  // Business Information
  operatingState: string; // Minnesota
  businessType: string; // SaaS, healthcare, etc.
  incorporationState: string; // Delaware
  foreignQualificationStatus?: 'not_needed' | 'needed' | 'in_progress' | 'completed';
  ein?: string; // Employer Identification Number
  dunsNumber?: string; // DUNS number
  yearsInBusiness?: number;
  legalStructure?: 'llc' | 'corporation' | 'partnership' | 'sole_proprietorship' | 'nonprofit';
  businessAddress?: string;
  businessPhone?: string;
  businessWebsite?: string;
  
  // Financial Information (for lender applications)
  currentRevenue?: number; // Current monthly/annual revenue
  projectedRevenue?: number; // Projected revenue
  currentExpenses?: number; // Current monthly expenses
  bankAccountBalance?: number; // Current bank balance
  existingDebt?: number; // Total existing debt
  monthlyDebtPayments?: number; // Monthly debt service
  creditScore?: number; // Business/personal credit score
  taxReturnsFiled?: boolean; // Whether tax returns have been filed
  yearsTaxReturnsFiled?: number; // Number of years of tax returns available
  
  // Personal/Founder Information (for lender applications)
  founderName?: string;
  founderTitle?: string;
  founderOwnership?: number; // Percentage ownership
  founderExperience?: string; // Years of experience, background
  personalCreditScore?: number;
  personalFinancialStatement?: boolean; // Whether personal financial statement is available
  
  // Business Plan Information
  executiveSummary?: string; // Executive summary for business plan
  marketAnalysis?: string; // Market size, opportunity, competition
  competitiveAdvantage?: string; // What makes the business unique
  businessModel?: string; // How the business makes money
  targetCustomers?: string; // Target market/customers
  salesStrategy?: string; // How you'll acquire customers
  managementTeam?: string; // Key team members and their backgrounds
  
  // Employment & Operations
  numberOfEmployees?: number;
  monthlyPayroll?: number;
  keyEmployees?: string; // List of key employees and roles
  
  // Collateral & Assets
  collateralDescription?: string; // What can be used as collateral
  assetsValue?: number; // Total value of business assets
  equipmentValue?: number; // Value of equipment/assets
  
  // Repayment Plan
  repaymentPlan?: string; // How you plan to repay the loan
  repaymentAbility?: string; // Explanation of ability to repay
  revenueProjections?: string; // Revenue projections for next 12-24 months
  
  // References & Documentation
  references?: string; // Business references (suppliers, customers, etc.)
  businessLicenses?: string; // Business licenses and permits held
  insuranceInfo?: string; // Business insurance information
  bankReferences?: string; // Banking relationships
  
  // Application Tracking
  applicationNumber?: string; // Lender's application reference
  lenderContactName?: string;
  lenderContactEmail?: string;
  lenderContactPhone?: string;
  lenderApplicationUrl?: string; // URL to lender's application portal
  
  // Documents & Notes
  documents: string[]; // Array of document URLs or IDs
  requiredDocuments?: string[]; // List of documents lender requires
  documentsSubmitted?: string[]; // Documents that have been submitted
  notes: string; // Internal notes
  lenderNotes?: string; // Notes from lender
  applicationFormData?: string; // Snapshot of data used for application (JSON or text)
  
  // Timeline
  expectedDecisionDate?: Date | Timestamp;
  nextFollowUpDate?: Date | Timestamp;
  
  // Metadata
  createdBy: string; // User ID
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
  updatedBy?: string;
}

export interface FundingApplicationFormData {
  program: FundingProgram;
  programName?: string;
  fundingType: FundingType;
  requestedAmount: number;
  useOfFunds: string;
  monthlyBurn?: number;
  cloudAiSubscriptions?: string;
  runwayMonths?: number;
  earliestRevenueTrigger?: string;
  operatingState: string;
  businessType: string;
  incorporationState: string;
  foreignQualificationStatus?: 'not_needed' | 'needed' | 'in_progress' | 'completed';
  
  // Business Information
  ein?: string;
  dunsNumber?: string;
  yearsInBusiness?: number;
  legalStructure?: 'llc' | 'corporation' | 'partnership' | 'sole_proprietorship' | 'nonprofit';
  businessAddress?: string;
  businessPhone?: string;
  businessWebsite?: string;
  
  // Financial Information
  currentRevenue?: number;
  projectedRevenue?: number;
  currentExpenses?: number;
  bankAccountBalance?: number;
  existingDebt?: number;
  monthlyDebtPayments?: number;
  creditScore?: number;
  taxReturnsFiled?: boolean;
  yearsTaxReturnsFiled?: number;
  
  // Founder Information
  founderName?: string;
  founderTitle?: string;
  founderOwnership?: number;
  founderExperience?: string;
  personalCreditScore?: number;
  personalFinancialStatement?: boolean;
  
  // Business Plan
  executiveSummary?: string;
  marketAnalysis?: string;
  competitiveAdvantage?: string;
  businessModel?: string;
  targetCustomers?: string;
  salesStrategy?: string;
  managementTeam?: string;
  
  // Employment & Operations
  numberOfEmployees?: number;
  monthlyPayroll?: number;
  keyEmployees?: string;
  
  // Collateral & Assets
  collateralDescription?: string;
  assetsValue?: number;
  equipmentValue?: number;
  
  // Repayment Plan
  repaymentPlan?: string;
  repaymentAbility?: string;
  revenueProjections?: string;
  
  // References & Documentation
  references?: string;
  businessLicenses?: string;
  insuranceInfo?: string;
  bankReferences?: string;
  
  // Application Tracking
  applicationNumber?: string;
  lenderContactName?: string;
  lenderContactEmail?: string;
  lenderContactPhone?: string;
  lenderApplicationUrl?: string;
  requiredDocuments?: string[];
  documentsSubmitted?: string[];
  
  notes?: string;
  expectedDecisionDate?: Date;
  nextFollowUpDate?: Date;
}

export const FundingProgramLabels: Record<FundingProgram, string> = {
  [FundingProgram.SBA_MICROLOAN]: 'SBA Microloan Program (via MN intermediaries)',
  [FundingProgram.CRF_USA]: 'Community Reinvestment Fund USA (CRF USA)',
  [FundingProgram.WOMENVENTURE]: 'WomenVenture',
  [FundingProgram.MEDA]: 'MEDA',
  [FundingProgram.NEON]: 'NEON',
  [FundingProgram.LAUNCH_MN]: 'Launch Minnesota',
  [FundingProgram.HELLO_ALICE]: 'Hello Alice',
  [FundingProgram.OTHER]: 'Other',
};

export const FundingApplicationStatusLabels: Record<FundingApplicationStatus, string> = {
  [FundingApplicationStatus.DRAFT]: 'Draft',
  [FundingApplicationStatus.SUBMITTED]: 'Submitted',
  [FundingApplicationStatus.UNDER_REVIEW]: 'Under Review',
  [FundingApplicationStatus.APPROVED]: 'Approved',
  [FundingApplicationStatus.REJECTED]: 'Rejected',
  [FundingApplicationStatus.WITHDRAWN]: 'Withdrawn',
  [FundingApplicationStatus.FUNDED]: 'Funded',
};

export const FundingTypeLabels: Record<FundingType, string> = {
  [FundingType.LOAN]: 'Loan',
  [FundingType.GRANT]: 'Grant',
  [FundingType.BOTH]: 'Both',
};

